import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  Dialog,
  TextField,
  Autocomplete,
  DialogTitle,
  DialogContent,
  DialogActions
} from "@mui/material";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import React from "react";
import { IInstrument } from "../../clients/answeringClient";
import { IAssignment } from "../../UserManagement/types";
import {
  CreateGroupModalController,
  CreateGroupModalControllerDelegate,
} from "./controller";

interface Props {
  delegate: CreateGroupModalDelegate;
}

interface AssigmentOption {
  value: number;
  label: string;
  instrumentId: string;
}

interface State {
  isLoading: boolean;
  name: string;
  assignments: IAssignment[];
  selectedAssignments: AssigmentOption[];
  // instrument
  instrumentOptions: IInstrument[];
  instrumentInput: string;
  instrumentLoading: boolean;
  selectedInstrument?: IInstrument | null;
}

export interface CreateGroupModalDelegate {
  shouldCloseCreateModal: () => void;
  shouldLogout: () => void;
  genericError: (error?: string) => void;
  didCreateGroup: (createdId: number) => void;
}

class CreateGroupModal
  extends React.Component<Props, State>
  implements CreateGroupModalControllerDelegate {
  private controller: CreateGroupModalController;
  private instrumentFetchDebounce?: NodeJS.Timeout;

  constructor(props: Props) {
    super(props);
    this.state = {
      isLoading: true,
      name: "",
      assignments: [],
      selectedAssignments: [],
      //instrument
      instrumentOptions: [],
      instrumentInput: "",
      instrumentLoading: false,
      selectedInstrument: null,
    };
    this.controller = new CreateGroupModalController(this);
  }

  // delegate
  didGetAssignments(assignments: IAssignment[]) {
    this.setState({
      isLoading: false,
      assignments,
    });
  }

  didCreateGroup(createdId: number) {
    this.props.delegate.didCreateGroup(createdId);
  }

  genericError(err?: string) {
    this.props.delegate.genericError(err);
  }

  applicationShouldLogout() {
    this.props.delegate.shouldLogout();
  }

  // internals
  handleChangeName(name: string) {
    this.setState({
      name: name.trim(),
    });
  }

  // search instruments
  private fetchInstruments(filter: string) {
    this.setState({ instrumentLoading: true });
    clearTimeout(this.instrumentFetchDebounce!);
    this.instrumentFetchDebounce = setTimeout(async () => {
      try {
        const token = localStorage.getItem("AccessToken")!;
        const instruments = await this.controller.getAvailableInstruments(
          token,
          { filter, limit: 40, page: 1 }
        );
        this.setState({ instrumentOptions: instruments });
      } catch (err) {
        console.error(err);
        this.setState({ instrumentOptions: [] });
      } finally {
        this.setState({ instrumentLoading: false });
      }
    }, 500);
  }

  private handleInstrumentInputChange = (_: any, value: string) => {
    this.setState({ instrumentInput: value }, () => {
      this.fetchInstruments(value);
    });
  };

  componentDidMount() {
    this.setState(
      {
        selectedInstrument: null,
        instrumentInput: "",
      },
      () => this.fetchInstruments("")
    );
  }

  didClickClose() {
    this.props.delegate.shouldCloseCreateModal();
  }

  didClickCreateGroup() {
    const token = localStorage.getItem("AccessToken")!;
    this.controller.createGroup(
      token,
      this.state.name,
      this.state.selectedAssignments.map((s) => s.value),
    );
  }

  renderLoading() {
    return (
      <Box
        alignItems="center"
        display="flex"
        flexDirection="column"
        marginTop={2}
      >
        <CircularProgress />
      </Box>
    );
  }

  renderForm() {
    const options = this.state.assignments
      .filter((assignment) =>
        this.state.selectedInstrument
          ? assignment.instrumentId === this.state.selectedInstrument.id
          : true
      )
      .map(
        (assignment) =>
        ({
          value: assignment.applicationId,
          label: assignment.title,
          instrumentId: assignment.instrumentId,
        } as AssigmentOption)
      );

    return (
      <Box
        alignItems="center"
        display="flex"
        flexDirection="column"
        gap={2}
      >
        <TextField
          variant="outlined"
          required
          label="Nome da devolutiva agregada"
          sx={{
            width: "100%",
          }}
          onChange={(event) => this.handleChangeName(event.target.value)}
        />

        <Autocomplete
          options={this.state.instrumentOptions}
          getOptionLabel={(opt) => opt.title}
          value={this.state.selectedInstrument}
          onChange={(_, newValue) => {
            this.setState({ selectedInstrument: newValue, assignments: [], selectedAssignments: [] });
            if (newValue) {
              const token = localStorage.getItem("AccessToken")!;
              this.controller.getManagedAssignments(token);
            }
          }}
          inputValue={this.state.instrumentInput}
          onInputChange={this.handleInstrumentInputChange}
          noOptionsText="Nenhum instrumento encontrado"
          loading={this.state.instrumentLoading}
          isOptionEqualToValue={(o, v) => o.id === v.id}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Pesquisar instrumento"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {this.state.instrumentLoading && (
                      <CircularProgress color="inherit" size={20} />
                    )}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          sx={{
            width: "100%",
          }}
        />

        <FormControl fullWidth>
          <Autocomplete
            multiple
            options={options}
            getOptionLabel={(opt) => opt.label}
            value={this.state.selectedAssignments}
            onChange={(_, newValue) =>
              this.setState({ selectedAssignments: newValue })
            }
            noOptionsText="Nenhuma aplicação encontrada neste instrumento"
            disabled={!this.state.selectedInstrument}
            isOptionEqualToValue={(o, v) => o.value === v.value}
            renderInput={(params) => (
              <TextField
                {...params}
                label={
                  this.state.selectedInstrument
                    ? "Selecione as aplicações"
                    : "Escolha um instrumento primeiro"
                }
              />
            )}

          />
        </FormControl>
      </Box>
    );
  }

  // Internals
  render() {
    return (
      <Dialog open onClose={() => this.didClickClose()} variant="modal" maxWidth="sm" fullWidth>
        <DialogTitle variant="modal">
          <GroupAddIcon />
          Criar devolutiva agregada
        </DialogTitle>
        <DialogContent variant="modal">{this.renderForm()}</DialogContent>
        <DialogActions variant="modal">
          <Button
            variant="outlined"
            color="error"
            onClick={() => this.didClickClose()}
          >
            Cancelar
          </Button>

          <Box
            component={Button}
            variant="contained"
            onClick={() => this.didClickCreateGroup()}
            disabled={
              !this.state.selectedAssignments.length || !this.state.name.length
            }
          >
            Criar devolutiva agregada
          </Box>
        </DialogActions>
      </Dialog>
    );
  }
}

export default CreateGroupModal;
